import type { WorkflowGraph } from '../../graph';
import type {
  OperationNodeSpec,
  FlowEdgeSpec,
  GroupSpec,
} from '../../graph/domain/types';
import type { Store } from '../types';
import { pushUndo } from './historyActions';
import { withSyncGuard } from '../sync/syncFlag';
import { analyzeGroupSubgraph, buildStepAdjacency } from '../../graph/domain/groupTopology';
import { generateId } from '../../graph/utils/id';

type SetState = (partial: Partial<Store> | ((s: Store) => Partial<Store>)) => void;
type GetState = () => Store;

// ── Delete plan ──────────────────────────────────────────────────────────────

export interface DeletePlan {
  operationsToRemove: string[];
  materialsToRemove: string[];
  edgesToRemove: string[];
  newEdges: Array<{
    kind: 'material-to-operation' | 'operation-to-material';
    sourceId: string;
    targetId: string;
  }>;
}

export interface MaterialDeletePlan {
  materialsToRemove: string[];
  operationsToRemove: string[];
  edgesToRemove: string[];
}

export interface BulkMaterialDeletePlan extends MaterialDeletePlan {
  selectedMaterialIds: string[];
}

function materialLabel(id: string, graphData: Store['graphData']): string {
  return graphData.materialNodes[id]?.name ?? id;
}

function operationLabel(id: string, graphData: Store['graphData']): string {
  return graphData.operationNodes[id]?.displayName ?? id;
}

function isProtectedRemainderMaterial(
  materialId: string,
  producerOperation: OperationNodeSpec,
  graphData: Store['graphData'],
  allEdges: FlowEdgeSpec[],
): boolean {
  const material = graphData.materialNodes[materialId];
  if (!material) return false;

  if (material.isReminder || material.symbolicQuantity === 'remainder') {
    return true;
  }

  // Legacy COMPLEX reminders may exist without isReminder/symbolic tags.
  // Keep them when they are actively consumed by a TRANSFER step.
  if (producerOperation.archetype === 'COMPLEX') {
    return allEdges.some(
      (edge) =>
        edge.sourceId === materialId &&
        graphData.operationNodes[edge.targetId]?.archetype === 'TRANSFER',
    );
  }

  return false;
}

export function computeDeletePlan(operationId: string, graphData: Store['graphData']): DeletePlan {
  const operation = graphData.operationNodes[operationId];
  if (!operation) throw new Error(`Operation not found: ${operationId}`);

  const allEdges = Object.values(graphData.flowEdges);

  const operationsToRemove = new Set<string>([operationId]);
  const materialsToRemove = new Set<string>();
  const materialQueue: string[] = [];

  // Seed with direct outputs of the deleted operation.
  for (const edge of allEdges) {
    if (edge.sourceId !== operationId) continue;
    if (!graphData.materialNodes[edge.targetId]) continue;
    if (materialsToRemove.has(edge.targetId)) continue;
    materialsToRemove.add(edge.targetId);
    materialQueue.push(edge.targetId);
  }

  // Fallback: TRANSFER post-state ids are canonical operation-owned outputs,
  // so include them even if flow edges are stale/missing.
  if (operation.archetype === 'TRANSFER' && operation.transferRow) {
    const transferOwnedIds = [
      operation.transferRow.sourcePostMaterialId,
      operation.transferRow.destinationPostMaterialId,
    ].filter((id): id is string => Boolean(id));

    for (const materialId of transferOwnedIds) {
      if (!graphData.materialNodes[materialId]) continue;
      if (materialsToRemove.has(materialId)) continue;
      materialsToRemove.add(materialId);
      materialQueue.push(materialId);
    }
  }

  // Fallback: legacy/stale graphs may miss op->reminder edges. If this is a
  // COMPLEX op, capture orphan reminder nodes that lineage-back to current inputs.
  if (operation.archetype === 'COMPLEX') {
    const inputMaterialIds = new Set(
      allEdges
        .filter((edge) => edge.targetId === operationId && Boolean(graphData.materialNodes[edge.sourceId]))
        .map((edge) => edge.sourceId),
    );

    if (inputMaterialIds.size > 0) {
      for (const reminderNode of Object.values(graphData.materialNodes)) {
        if (!reminderNode?.id) continue;
        if (!reminderNode.isReminder && reminderNode.symbolicQuantity !== 'remainder') continue;

        const upstreamSourceMatch = (reminderNode.sourceMaterials ?? []).some((src) =>
          src?.sourceVertexId ? inputMaterialIds.has(src.sourceVertexId) : false,
        );
        if (!upstreamSourceMatch) continue;

        const producerOpIds = allEdges
          .filter((edge) => edge.targetId === reminderNode.id && Boolean(graphData.operationNodes[edge.sourceId]))
          .map((edge) => edge.sourceId);

        if (producerOpIds.length > 0 && !producerOpIds.includes(operationId)) {
          continue;
        }

        if (materialsToRemove.has(reminderNode.id)) continue;
        materialsToRemove.add(reminderNode.id);
        materialQueue.push(reminderNode.id);
      }
    }
  }

  // Cascade through downstream consumers: any operation that consumes a doomed
  // material is removed, along with all of that operation's output materials.
  let queueIndex = 0;
  while (queueIndex < materialQueue.length) {
    const currentMaterialId = materialQueue[queueIndex++];

    for (const edge of allEdges) {
      if (edge.sourceId !== currentMaterialId) continue;
      if (!graphData.operationNodes[edge.targetId]) continue;

      const consumerOperationId = edge.targetId;
      const isNewOperation = !operationsToRemove.has(consumerOperationId);
      operationsToRemove.add(consumerOperationId);
      if (!isNewOperation) continue;

      for (const outEdge of allEdges) {
        if (outEdge.sourceId !== consumerOperationId) continue;
        if (!graphData.materialNodes[outEdge.targetId]) continue;
        if (materialsToRemove.has(outEdge.targetId)) continue;
        materialsToRemove.add(outEdge.targetId);
        materialQueue.push(outEdge.targetId);
      }
    }
  }

  const edgesToRemove = new Set<string>();
  for (const [flowEdgeId, edge] of Object.entries(graphData.flowEdges)) {
    const touchesRemovedMaterial =
      materialsToRemove.has(edge.sourceId) || materialsToRemove.has(edge.targetId);
    const touchesRemovedOperation =
      operationsToRemove.has(edge.sourceId) || operationsToRemove.has(edge.targetId);

    if (touchesRemovedMaterial || touchesRemovedOperation) {
      edgesToRemove.add(edge.id ?? flowEdgeId);
    }
  }

  return {
    operationsToRemove: Array.from(operationsToRemove),
    materialsToRemove: Array.from(materialsToRemove),
    edgesToRemove: Array.from(edgesToRemove),
    newEdges: [],
  };
}

export function computeMaterialDeletePlan(
  materialId: string,
  graphData: Store['graphData'],
): MaterialDeletePlan {
  if (!graphData.materialNodes[materialId]) {
    throw new Error(`Material not found: ${materialId}`);
  }

  const materialsToRemove = new Set<string>();
  const operationsToRemove = new Set<string>();
  const materialQueue: string[] = [];
  const allEdges = Object.values(graphData.flowEdges);

  const addMaterial = (id: string) => {
    if (!graphData.materialNodes[id]) return;
    if (materialsToRemove.has(id)) return;
    materialsToRemove.add(id);
    materialQueue.push(id);
  };

  const addOperation = (id: string) => {
    if (!graphData.operationNodes[id]) return;
    operationsToRemove.add(id);
  };

  // Seed with the selected material.
  addMaterial(materialId);

  // Upstream (one hop): remove producing operation(s) and all of their outputs.
  for (const edge of allEdges) {
    if (edge.targetId !== materialId) continue;
    if (!graphData.operationNodes[edge.sourceId]) continue;
    addOperation(edge.sourceId);
  }

  for (const operationId of Array.from(operationsToRemove)) {
    const producerOperation = graphData.operationNodes[operationId];
    if (!producerOperation) continue;

    for (const edge of allEdges) {
      if (edge.sourceId !== operationId) continue;
      if (!graphData.materialNodes[edge.targetId]) continue;
      addMaterial(edge.targetId);
    }
  }

  // Downstream (transitive): for every doomed material, remove all consumer
  // operations, then all materials produced by those operations.
  let queueIndex = 0;
  while (queueIndex < materialQueue.length) {
    const currentMaterialId = materialQueue[queueIndex++];

    for (const edge of allEdges) {
      if (edge.sourceId !== currentMaterialId) continue;
      if (!graphData.operationNodes[edge.targetId]) continue;

      const consumerOperationId = edge.targetId;
      const isNewOperation = !operationsToRemove.has(consumerOperationId);
      addOperation(consumerOperationId);
      if (!isNewOperation) continue;

      for (const outEdge of allEdges) {
        if (outEdge.sourceId !== consumerOperationId) continue;
        if (!graphData.materialNodes[outEdge.targetId]) continue;
        addMaterial(outEdge.targetId);
      }
    }
  }

  // Remove every edge touching any doomed material or operation.
  const edgesToRemove = new Set<string>();
  for (const [flowEdgeId, edge] of Object.entries(graphData.flowEdges)) {
    const touchesRemovedMaterial =
      materialsToRemove.has(edge.sourceId) || materialsToRemove.has(edge.targetId);
    const touchesRemovedOperation =
      operationsToRemove.has(edge.sourceId) || operationsToRemove.has(edge.targetId);

    if (touchesRemovedMaterial || touchesRemovedOperation) {
      edgesToRemove.add(edge.id ?? flowEdgeId);
    }
  }

  return {
    materialsToRemove: Array.from(materialsToRemove),
    operationsToRemove: Array.from(operationsToRemove),
    edgesToRemove: Array.from(edgesToRemove),
  };
}

function uniqueOrdered(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function sortLex(ids: Iterable<string>): string[] {
  return Array.from(ids).sort((a, b) => a.localeCompare(b));
}

export function computeBulkMaterialDeletePlan(
  materialIds: string[],
  graphData: Store['graphData'],
): BulkMaterialDeletePlan {
  const selectedMaterialIds = uniqueOrdered(materialIds.filter(Boolean));
  if (selectedMaterialIds.length === 0) {
    throw new Error('No materials selected for deletion');
  }

  for (const materialId of selectedMaterialIds) {
    if (!graphData.materialNodes[materialId]) {
      throw new Error(`Material not found: ${materialId}`);
    }
  }

  const materialsToRemove = new Set<string>();
  const operationsToRemove = new Set<string>();
  const edgesToRemove = new Set<string>();

  // Aggregate per-material plans from the same graph snapshot so behavior
  // mirrors repeated single deletes but remains deterministic and order-independent.
  for (const materialId of selectedMaterialIds) {
    const plan = computeMaterialDeletePlan(materialId, graphData);
    for (const id of plan.materialsToRemove) materialsToRemove.add(id);
    for (const id of plan.operationsToRemove) operationsToRemove.add(id);
    for (const id of plan.edgesToRemove) edgesToRemove.add(id);
  }

  return {
    selectedMaterialIds,
    materialsToRemove: sortLex(materialsToRemove),
    operationsToRemove: sortLex(operationsToRemove),
    edgesToRemove: sortLex(edgesToRemove),
  };
}

function pruneGroupsAfterOperationRemoval(
  groups: Record<string, GroupSpec>,
  removedOperationIds: Set<string>,
): Record<string, GroupSpec> {
  const nextGroups: Record<string, GroupSpec> = {};

  for (const [groupId, group] of Object.entries(groups)) {
    const removedIndices = group.stepIds
      .map((stepId, index) => (removedOperationIds.has(stepId) ? index : -1))
      .filter((index) => index >= 0);

    if (removedIndices.length === 0) {
      nextGroups[groupId] = group;
      continue;
    }

    const newStepIds = group.stepIds.filter((stepId) => !removedOperationIds.has(stepId));
    if (newStepIds.length === 0) {
      continue;
    }

    const oldIndexToNewIndex = new Map<number, number>();
    let nextIndex = 0;
    for (let index = 0; index < group.stepIds.length; index++) {
      if (!removedOperationIds.has(group.stepIds[index])) {
        oldIndexToNewIndex.set(index, nextIndex++);
      }
    }

    const adjustedCycles = (group.cycleRanges ?? []).flatMap((cycle) => {
      const keptIndices: number[] = [];
      for (let index = cycle.startIndex; index <= cycle.endIndex; index++) {
        const mapped = oldIndexToNewIndex.get(index);
        if (mapped != null) keptIndices.push(mapped);
      }
      if (keptIndices.length === 0) return [];
      return [{
        ...cycle,
        startIndex: Math.min(...keptIndices),
        endIndex: Math.max(...keptIndices),
      }];
    });

    nextGroups[groupId] = {
      ...group,
      stepIds: newStepIds,
      cycleRanges: adjustedCycles,
    };
  }

  return nextGroups;
}

/**
 * Find all connected components within a set of operation IDs.
 * Returns an array of arrays, each representing a connected component.
 */
function findConnectedComponents(
  stepIds: string[],
  graphData: Store['graphData'],
): string[][] {
  if (stepIds.length === 0) return [];
  if (stepIds.length === 1) return [stepIds];

  const adjacency = buildStepAdjacency(graphData);
  const visited = new Set<string>();
  const components: string[][] = [];

  for (const stepId of stepIds) {
    if (visited.has(stepId)) continue;

    // BFS to find connected component
    const component: string[] = [];
    const queue = [stepId];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;

      visited.add(current);
      component.push(current);

      // Add neighbors that are in stepIds
      const neighbors = adjacency.get(current) || new Set();
      for (const neighbor of neighbors) {
        if (stepIds.includes(neighbor) && !visited.has(neighbor)) {
          queue.push(neighbor);
        }
      }
    }

    components.push(component);
  }

  return components;

}



// ── Store action factory ─────────────────────────────────────────────────────

export function createDeleteActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    computeDeletePlan(operationId: string): DeletePlan {
      return computeDeletePlan(operationId, get().graphData);
    },

    deleteOperationStep(operationId: string): void {
      const state = get();
      const operation = state.graphData.operationNodes[operationId];
      if (!operation) throw new Error(`Operation not found: ${operationId}`);

      pushUndo(get, set, `Delete ${operation.displayName ?? 'operation'}`);

      const plan = computeDeletePlan(operationId, state.graphData);
      const removedOperationIds = new Set(plan.operationsToRemove);

      set((s) => {
        const flowEdges = { ...s.graphData.flowEdges };
        const materialNodes = { ...s.graphData.materialNodes };
        const operationNodes = { ...s.graphData.operationNodes };

        // 1. Remove marked edges
        for (const edgeId of plan.edgesToRemove) {
          delete flowEdges[edgeId];
        }

        // 2. Remove output/downstream materials
        for (const matId of plan.materialsToRemove) {
          delete materialNodes[matId];
        }

        // 3. Remove operation and downstream operations
        for (const opId of plan.operationsToRemove) {
          delete operationNodes[opId];
        }

        // 4. Auto-adjust groups and cycles
        const groups = pruneGroupsAfterOperationRemoval(
          s.graphData.groups,
          removedOperationIds,
        );

        return {
          graphData: { ...s.graphData, flowEdges, materialNodes, operationNodes, groups },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        const graph = getGraph();
        if (!graph) return;

        graph.batchUpdate(() => {
          const model = graph.getDataModel();

          // Remove stale edges using model.remove (NOT graph.removeCells)
          // because we are inside batchUpdate — nested transactions must be avoided
          for (const edgeId of plan.edgesToRemove) {
            const cell = model.getCell(edgeId);
            if (cell) model.remove(cell);
          }

          // Remove output material vertices
          for (const matId of plan.materialsToRemove) {
            const cell = model.getCell(matId);
            if (cell) model.remove(cell);
          }

          // Remove all operation cells in the cascade plus collapsed edge cells.
          for (const opId of plan.operationsToRemove) {
            const collapsedEdgeIds = [
              `op-edge-${opId}`,
              `op-edge-${opId}-transfer`,
              `op-edge-${opId}-remainder`,
              `op-edge-${opId}-dest`,
            ];
            for (const edgeId of collapsedEdgeIds) {
              const collapsedEdgeCell = model.getCell(edgeId);
              if (collapsedEdgeCell) model.remove(collapsedEdgeCell);
            }
            const opCell = model.getCell(opId);
            if (opCell) model.remove(opCell);
          }
          // LayoutManager fires once after this batchUpdate completes
        });
      });
    },

    deleteMaterial(materialId: string): void {
      const state = get();
      const material = state.graphData.materialNodes[materialId];
      if (!material) throw new Error(`Material not found: ${materialId}`);

      const plan = computeMaterialDeletePlan(materialId, state.graphData);

      pushUndo(get, set, `Delete ${material.name ?? 'material'}`);

      set((s) => {
        const flowEdges = { ...s.graphData.flowEdges };
        const materialNodes = { ...s.graphData.materialNodes };
        const operationNodes = { ...s.graphData.operationNodes };

        for (const edgeId of plan.edgesToRemove) {
          delete flowEdges[edgeId];
        }

        for (const removedMaterialId of plan.materialsToRemove) {
          delete materialNodes[removedMaterialId];
        }

        for (const removedOperationId of plan.operationsToRemove) {
          delete operationNodes[removedOperationId];
        }

        const groups = pruneGroupsAfterOperationRemoval(
          s.graphData.groups,
          new Set(plan.operationsToRemove),
        );

        return {
          graphData: { ...s.graphData, flowEdges, materialNodes, operationNodes, groups },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        const graph = getGraph();
        if (!graph) return;

        graph.batchUpdate(() => {
          const model = graph.getDataModel();

          for (const edgeId of plan.edgesToRemove) {
            const cell = model.getCell(edgeId);
            if (cell) model.remove(cell);
          }

          for (const operationId of plan.operationsToRemove) {
            const collapsedEdgeIds = [
              `op-edge-${operationId}`,
              `op-edge-${operationId}-transfer`,
              `op-edge-${operationId}-remainder`,
              `op-edge-${operationId}-dest`,
            ];

            for (const edgeId of collapsedEdgeIds) {
              const collapsedEdgeCell = model.getCell(edgeId);
              if (collapsedEdgeCell) model.remove(collapsedEdgeCell);
            }
          }

          for (const removedMaterialId of plan.materialsToRemove) {
            const materialCell = model.getCell(removedMaterialId);
            if (materialCell) model.remove(materialCell);
          }

          for (const removedOperationId of plan.operationsToRemove) {
            const operationCell = model.getCell(removedOperationId);
            if (operationCell) model.remove(operationCell);
          }
        });
      });
    },

    deleteMaterials(materialIds: string[]): void {
      const state = get();
      const plan = computeBulkMaterialDeletePlan(materialIds, state.graphData);

      const selectedNames = plan.selectedMaterialIds
        .map((id) => state.graphData.materialNodes[id]?.name ?? id)
        .join(', ');
      pushUndo(get, set, `Delete materials: ${selectedNames}`);

      set((s) => {
        const flowEdges = { ...s.graphData.flowEdges };
        const materialNodes = { ...s.graphData.materialNodes };
        const operationNodes = { ...s.graphData.operationNodes };

        for (const edgeId of plan.edgesToRemove) {
          delete flowEdges[edgeId];
        }

        for (const removedMaterialId of plan.materialsToRemove) {
          delete materialNodes[removedMaterialId];
        }

        for (const removedOperationId of plan.operationsToRemove) {
          delete operationNodes[removedOperationId];
        }

        const groups = pruneGroupsAfterOperationRemoval(
          s.graphData.groups,
          new Set(plan.operationsToRemove),
        );

        return {
          graphData: { ...s.graphData, flowEdges, materialNodes, operationNodes, groups },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        const graph = getGraph();
        if (!graph) return;

        graph.batchUpdate(() => {
          const model = graph.getDataModel();

          for (const edgeId of plan.edgesToRemove) {
            const cell = model.getCell(edgeId);
            if (cell) model.remove(cell);
          }

          for (const operationId of plan.operationsToRemove) {
            const collapsedEdgeIds = [
              `op-edge-${operationId}`,
              `op-edge-${operationId}-transfer`,
              `op-edge-${operationId}-remainder`,
              `op-edge-${operationId}-dest`,
            ];

            for (const edgeId of collapsedEdgeIds) {
              const collapsedEdgeCell = model.getCell(edgeId);
              if (collapsedEdgeCell) model.remove(collapsedEdgeCell);
            }
          }

          for (const removedMaterialId of plan.materialsToRemove) {
            const materialCell = model.getCell(removedMaterialId);
            if (materialCell) model.remove(materialCell);
          }

          for (const removedOperationId of plan.operationsToRemove) {
            const operationCell = model.getCell(removedOperationId);
            if (operationCell) model.remove(operationCell);
          }
        });
      });
    },
  };
}
